import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * MethodSubjectField — Renders ONLY when the selected course is B.Ed.
 * Used in Student Admission, Online Admission, Booking, Bulk Upload forms.
 * 
 * Props:
 *  - courseId: currently-selected course id
 *  - courses: list of courses (each must have id, name OR course_name, code)
 *  - value: current method_subject value
 *  - onChange: (newValue) => void
 *  - apiBase: optional API base URL (defaults to REACT_APP_BACKEND_URL)
 *  - publicMode: if true, uses unauthenticated public endpoint (for Public Admission Form)
 */
export const isBEdCourse = (course) => {
  if (!course) return false;
  const name = (course.name || course.course_name || '').toLowerCase();
  const code = (course.code || course.course_code || '').toLowerCase();
  return name.includes('b.ed') || name.includes('bed ') || name === 'bed' ||
         name.includes('bachelor of education') ||
         code === 'bed' || code === 'b.ed' || code.startsWith('bed');
};

const MethodSubjectField = ({ courseId, courses = [], value, onChange, publicMode = false, tenantSubdomain = '' }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const selectedCourse = (courses || []).find(c => c.id === courseId);
  const show = isBEdCourse(selectedCourse);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    const url = publicMode
      ? `${API_BASE}/api/online-admissions/method-subjects/${tenantSubdomain}`
      : `${API_BASE}/api/master-settings/method-subjects`;
    const headers = publicMode ? {} : { Authorization: `Bearer ${localStorage.getItem('token')}` };
    axios.get(url, { headers })
      .then(res => { if (!cancelled) setOptions(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, API_BASE, publicMode, tenantSubdomain]);

  if (!show) return null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Method Subject <span className="text-xs text-gray-500">(Optional, for B.Ed)</span>
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        data-testid="method-subject-select"
      >
        <option value="">-- Select Method Subject --</option>
        {options.map(opt => (
          <option key={opt.id} value={opt.name}>{opt.name}</option>
        ))}
      </select>
      {loading && <p className="text-xs text-gray-500 mt-1">Loading options...</p>}
      {!loading && options.length === 0 && (
        <p className="text-xs text-amber-600 mt-1">
          No method subjects configured yet. Admin can add them in Master Settings → Method Subjects.
        </p>
      )}
    </div>
  );
};

export default MethodSubjectField;
